import React, { useEffect, useState } from 'react';
import {
  paymentProvidersService,
  type PaymentProviderConfig,
} from '@/api/services/paymentProviders';
import { formatApiError } from '@/utils/formatApiError';
import { Toggle } from '@/components/ui';
import { DataTableScrollArea } from '@/components/table';

type DraftSecrets = Record<string, string>;

export default function PaymentProvidersPage() {
  const [providers, setProviders] = useState<PaymentProviderConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState<string | null>(null);
  const [secretDrafts, setSecretDrafts] = useState<Record<string, DraftSecrets>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await paymentProvidersService.list();
      setProviders(res.items);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateProvider = async (provider: PaymentProviderConfig) => {
    try {
      const secrets = secretDrafts[provider.provider] ?? {};
      await paymentProvidersService.upsert(provider.provider, {
        mode: provider.mode,
        isEnabled: provider.isEnabled,
        publicConfig: provider.publicConfig,
        secretConfig: secrets,
      });
      setSecretDrafts((prev) => ({ ...prev, [provider.provider]: {} }));
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const runTest = async (provider: string) => {
    setTesting(provider);
    try {
      const result = await paymentProvidersService.test(provider);
      setError(result.ok ? null : result.message ?? 'Provider test failed');
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setTesting(null);
    }
  };

  if (loading) return <div className="panel">Loading payment providers…</div>;

  return (
    <div className="panel">
      <h1>Payment providers</h1>
      <p className="muted">
        Configure provider-neutral payment infrastructure. PayPal is reserved for a future adapter;
        online checkout remains unavailable until that adapter is implemented and enabled.
      </p>
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="data-table-wrap">
        <DataTableScrollArea>
        <table className="data-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Enabled</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Public config</th>
              <th>Secrets (leave blank to keep)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.provider}>
                <td>
                  <strong>{p.displayName}</strong>
                  <div className="muted">{p.provider}</div>
                </td>
                <td>
                  <Toggle
                    compact
                    checked={p.isEnabled}
                    onChange={(next) =>
                      setProviders((rows) =>
                        rows.map((row) =>
                          row.provider === p.provider ? { ...row, isEnabled: next } : row,
                        ),
                      )
                    }
                    aria-label={`Enable ${p.displayName}`}
                  />
                </td>
                <td>
                  <select
                    value={p.mode}
                    onChange={(e) =>
                      setProviders((rows) =>
                        rows.map((row) =>
                          row.provider === p.provider ? { ...row, mode: e.target.value } : row
                        )
                      )
                    }
                  >
                    <option value="test">test</option>
                    <option value="live">live</option>
                  </select>
                </td>
                <td>
                  {p.status}
                  {p.lastTestStatus ? (
                    <div className="muted">
                      last test: {p.lastTestStatus}
                      {p.lastTestError ? ` — ${p.lastTestError}` : ''}
                    </div>
                  ) : null}
                </td>
                <td>
                  {p.provider === 'paypal' ? (
                    <>
                      <label className="muted">client_id</label>
                      <input
                        value={p.publicConfig.client_id ?? ''}
                        onChange={(e) =>
                          setProviders((rows) =>
                            rows.map((row) =>
                              row.provider === p.provider
                                ? {
                                    ...row,
                                    publicConfig: { ...row.publicConfig, client_id: e.target.value },
                                  }
                                : row
                            )
                          )
                        }
                      />
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  <span className="muted">{JSON.stringify(p.secretConfig)}</span>
                </td>
                <td>
                  <button type="button" className="btn" onClick={() => updateProvider(p)}>
                    Save
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={testing === p.provider}
                    onClick={() => runTest(p.provider)}
                  >
                    {testing === p.provider ? 'Testing…' : 'Test'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </DataTableScrollArea>
      </div>
    </div>
  );
}
